import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";

/**
 * Minimal monochrome SVGs for X, Facebook, Telegram, TikTok.
 * Official production accounts of the SFN / MealToken24 ecosystem.
 */
export const SFN_SOCIALS = [
  {
    id: "x",
    label: "X (Twitter)",
    href: "https://x.com/Neutral_Frame",
    path: "M18.244 2H21.5l-7.51 8.578L23 22h-6.86l-5.376-7.027L4.7 22H1.44l8.03-9.176L1 2h7.034l4.86 6.43L18.244 2Zm-2.404 18h1.9L7.26 4H5.25l10.59 16Z",
  },
  {
    id: "facebook",
    label: "Facebook",
    href: "https://facebook.com/profile.php?id=61591822198122",
    path: "M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.45 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94Z",
  },
  {
    id: "telegram",
    label: "Telegram",
    href: "https://t.me/MealToken24",
    path: "M21.94 4.4 18.79 19.3c-.24 1.07-.87 1.33-1.76.83l-4.86-3.58-2.34 2.25c-.26.26-.48.48-.98.48l.35-4.95L17.27 7c.39-.35-.09-.55-.6-.2L7.55 12.54l-4.88-1.53c-1.06-.33-1.08-1.06.22-1.57L20.5 3.06c.88-.32 1.65.21 1.44 1.34Z",
  },
  {
    id: "tiktok",
    label: "TikTok",
    href: "https://tiktok.com/@informer2007",
    path: "M16.5 2h-2.9v13.2a2.6 2.6 0 1 1-2.6-2.6c.2 0 .4 0 .6.06V9.7a5.6 5.6 0 1 0 4.9 5.55V8.9a6.6 6.6 0 0 0 3.9 1.26V7.24A3.74 3.74 0 0 1 16.5 3.6V2Z",
  },
  {
    id: "youtube",
    label: "YouTube",
    href: "https://www.youtube.com/@MealToken24",
    path: "M23 12s0-3.2-.41-4.74a2.5 2.5 0 0 0-1.76-1.77C19.29 5.08 12 5.08 12 5.08s-7.29 0-8.83.41A2.5 2.5 0 0 0 1.41 7.26C1 8.8 1 12 1 12s0 3.2.41 4.74a2.5 2.5 0 0 0 1.76 1.77c1.54.41 8.83.41 8.83.41s7.29 0 8.83-.41a2.5 2.5 0 0 0 1.76-1.77C23 15.2 23 12 23 12ZM9.75 15.02V8.98L15.5 12l-5.75 3.02Z",
  },
  {
    id: "instagram",
    label: "Instagram",
    path: "M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.8 3.8 0 0 1-1.38-.9 3.8 3.8 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16Zm0 3.29A6.55 6.55 0 1 0 18.55 12 6.55 6.55 0 0 0 12 5.45Zm0 10.8A4.25 4.25 0 1 1 16.25 12 4.25 4.25 0 0 1 12 16.25Zm6.81-10.98a1.53 1.53 0 1 1-1.53-1.53 1.53 1.53 0 0 1 1.53 1.53Z",
    href: "https://www.instagram.com/mealtoken24",
  },
] as const;


type SocialIconsProps = {
  surface: string;
  size?: "sm" | "md";
  className?: string;
};

/**
 * Compact social icon row with a golden-green hover glow.
 * Placeholder `href`s map 1:1 to the official ecosystem accounts.
 */
export function SocialIcons({ surface, size = "md", className }: SocialIconsProps) {
  // WCAG 2.5.5: every target is at least 44x44 CSS px.
  const px = size === "sm" ? "h-11 w-11" : "h-12 w-12";
  const ic = size === "sm" ? "h-5 w-5" : "h-6 w-6";
  return (
    <ul
      className={cn("relative z-10 flex items-center gap-2", className)}
      aria-label="Social media"
    >
      {SFN_SOCIALS.map((s) => (
        <li key={s.id} className="pointer-events-auto">
          <a
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={s.label}
            title={s.label}
            onClick={() => track("cta_click", { id: `social_${s.id}`, surface })}
            className={cn(
              "group/social relative z-10 grid min-h-11 min-w-11 cursor-pointer place-items-center rounded-full border border-white/10 bg-white/[0.03] text-foreground/80 transition-all duration-300",
              "hover:text-obsidian hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60",
              "hover:border-[#DEFF9A] hover:bg-gradient-to-br hover:from-[#FFD66B] hover:to-[#DEFF9A]",
              "hover:shadow-[0_0_22px_rgba(222,255,154,0.55),0_0_4px_rgba(255,214,107,0.6)]",
              px,
            )}
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
              focusable="false"
              className={cn("pointer-events-none", ic)}
            >
              <path d={s.path} />
            </svg>
          </a>
        </li>
      ))}
    </ul>
  );
}
